import React, { useState, useEffect } from "react";

import { makeStyles, withStyles } from "@mui/styles";
import { DateRangePicker } from "rsuite";
import moment from "moment-timezone";

import "rsuite/dist/rsuite.css";
import "./index.styles.css";
import { PredefinedRangeInterface } from "./index.types";

import { useSelector } from "store";

export declare type DateRange = [Date, Date];

type DatePickerRange<T extends React.ElementType> = {
  onOk?: (date: DateRange, event: React.SyntheticEvent) => void;
  onChange: (date: string[], event?: React.SyntheticEvent) => void;
} & React.ComponentPropsWithoutRef<T>;

const CSSDateRangePicker = withStyles({
  root: () => ({
    "& .rs-picker-daterange .rs-picker-toggle.rs-btn .rs-picker-toggle-caret ":
      {
        color: "#DB154D !important",
        width: "15px !important",
        height: "16px !important",
      },
    "& .rs-picker-menu": {
      marginTop: "20px !important",
      zIndex: "9999 !important",
    },
  }),
})(DateRangePicker);

const useStyles = makeStyles(() => ({
  rootClasses: {
    "& .rs-picker-menu": {
      marginTop: "20px !important",
      zIndex: "-1 !important",
    },
    "& .rs-anim-fade.rs-anim-in.rs-picker-daterange-menu.rs-picker-menu.placement-bottom-start":
      {
        marginTop: "20px !important",
      },
    width: 220,
    background: " #FAFAFA",

    borderRadius: "8px",
    "& .rs-picker-toggle-caret": {
      color: "#DB154D !important",
      width: "15px !important",
      height: "16px !important",
      padding: "12px 16px important",
    },
  },
}));

const getPredefinedRanges = (): PredefinedRangeInterface[] => [
  {
    label: "Last 24-48 hours",
    value: [moment().subtract(2, "days").toDate(), moment().toDate()],
    placement: "left",
  },
  {
    label: "Today",
    value: [moment().toDate(), moment().toDate()],
    placement: "left",
  },
  {
    label: "Yesterday",
    value: [
      moment().subtract(1, "day").startOf("day").toDate(),
      moment().subtract(1, "day").endOf("day").toDate(),
    ],
    placement: "left",
  },
  {
    label: "Last 7 days",
    value: [moment().subtract(6, "days").toDate(), moment().toDate()],
    placement: "left",
  },
  {
    label: "Last 30 days",
    value: [moment().subtract(29, "days").toDate(), moment().toDate()],
    placement: "left",
  },
  {
    label: "This month",
    value: [moment().startOf("month").toDate(), moment().toDate()],
    placement: "left",
  },
  {
    label: "Last month",
    value: [
      moment().subtract(1, "month").startOf("month").toDate(),
      moment().subtract(1, "month").endOf("month").toDate(),
    ],
    placement: "left",
  },
  {
    label: "Last 3 months",
    value: [
      moment().subtract(3, "month").startOf("month").toDate(),
      moment().subtract(1, "month").endOf("month").toDate(),
    ],
    placement: "left",
  },
  {
    label: "This year",
    value: [moment().startOf("year").toDate(), moment().toDate()],
    placement: "left",
  },
];

const { afterToday } = DateRangePicker;

const DatePicker = <T extends React.ElementType = "input">({
  onOk,
  onChange,
}: DatePickerRange<T>): JSX.Element => {
  const classes = useStyles();
  const { startDate, endDate } = useSelector((state) => state.global);

  // pre-defined ranges
  const [predefinedRanges, setPredefinedRanges] = useState<
    PredefinedRangeInterface[]
  >([]);

  // current moment
  const [currentMoment, setCurrentMoment] = useState(moment());

  // set current moment
  const handleOpen = () => {
    setCurrentMoment(moment());
  };

  // set new pre-defined date ranges on change in current moment
  useEffect(() => {
    setPredefinedRanges(getPredefinedRanges());
  }, [currentMoment]);

  return (
    <CSSDateRangePicker
      format="MMM dd yyyy"
      character=" - "
      ranges={predefinedRanges}
      value={
        startDate && endDate
          ? [moment(startDate).toDate(), moment(endDate).toDate()]
          : predefinedRanges[0].value
      }
      className={classes.rootClasses}
      onOk={onOk}
      cleanable={false}
      onChange={onChange}
      onOpen={handleOpen}
      // TODO: use this attribute if need to disable future dates
      // disabledDate={afterToday ? afterToday() : () => {}}
      // makeStyles-rootClasses-1
      style={{
        ".rs-picker-menu ": {
          marginTop: "20px !important",
          zIndex: "-1 !important",
        },
        // "rs-picker-toggle-wrapper": {
        //   visibility: "hidden !important",
        // },
      }}
    />
  );
};

export default DatePicker;
